import type { useThemeTokens } from "@/lib/theme/tokens";
import type { useInteractiveTokens } from "@/lib/theme/tokens/interactive";

/** Marks nodes that need a solid classic surface (toast, modal, drawer). */
export const CLASSIC_PORTALED_SURFACE_ATTR = "data-classic-portaled-surface";

type ThemeTokens = ReturnType<typeof useThemeTokens>;
type InteractiveTokens = ReturnType<typeof useInteractiveTokens>;

export type PortaledSurfaceMode = "modal" | "glass" | "menu" | "toast";

/**
 * Surface props for portaled UI (menus, modals, toasts).
 * Classic appearance disables backdrop-filter globally - solid bg required.
 * Toasts always skip blur: Chakra's motion wrapper uses transform, which
 * intermittently kills backdrop-filter and leaves a see-through ghost card.
 */
export function getPortaledSurfaceProps(
  theme: ThemeTokens,
  interactive: InteractiveTokens,
  mode: PortaledSurfaceMode = "modal",
) {
  const useSolidSurface = interactive.backdrop.blur === "none";

  if (mode === "toast") {
    return {
      bg: useSolidSurface
        ? theme.surfaceGlass.cardStrong
        : theme.mode === "dark"
          ? "rgba(16, 18, 22, 0.97)"
          : "rgba(255, 255, 255, 0.97)",
      backdropFilter: "none" as const,
      ...(useSolidSurface ? { [CLASSIC_PORTALED_SURFACE_ATTR]: "" } : {}),
    };
  }

  if (useSolidSurface) {
    return {
      bg: theme.surfaceGlass.cardStrong,
      backdropFilter: "none" as const,
      [CLASSIC_PORTALED_SURFACE_ATTR]: "",
    };
  }

  switch (mode) {
    case "glass":
      return {
        bg: theme.surfaceGlass.cardStrong,
        backdropFilter: interactive.backdrop.blur,
      };
    case "menu":
      return {
        bg: "none",
        backdropFilter: interactive.backdrop.blur,
      };
    case "modal":
    default:
      return {
        bg: theme.mode === "light" ? "#f6f8fb" : "none",
        backdropFilter: interactive.backdrop.blur,
      };
  }
}
